import { Box, type SxProps, Typography } from '@mui/material';
import { useState } from 'react';
import { LoadingErrorWrapper } from '@/admins/SmartReports/components/Components/Common/LoadingErrorWrapper';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import type { ComponentDefinition, ComponentEditProps } from '../../types';
import { styles } from './styles';
import { TextAreaView } from './TextAreaView';
import type { TextAreaData, TextAreaSection } from './types';

export const COMPONENT_ID = 'text_area';

export const TextArea = ({
  persistedData,
  onComponentLoad,
  sx,
}: ComponentEditProps<TextAreaData>) => {
  const [data, setData] = useState<TextAreaData>(
    persistedData || { sections: [{ title: '', content: '' }] },
  );

  // Helper function to call onComponentLoad with consistent data structure
  const notifyComponentLoad = (newData: TextAreaData): void => {
    if (onComponentLoad && newData.sections && newData.sections.length > 0) {
      onComponentLoad({
        componentId: COMPONENT_ID,
        data: newData,
      });
    }
  };

  const addSection = (): void => {
    if (data.sections.length < 4) {
      const newSections = [...data.sections, { title: '', content: '' }];
      const newData = { ...data, sections: newSections };
      setData(newData);
      notifyComponentLoad(newData);
    }
  };

  const removeSection = (index: number): void => {
    if (data.sections.length > 1) {
      const newSections = data.sections.filter((_, i) => i !== index);
      const newData = { ...data, sections: newSections };
      setData(newData);
      notifyComponentLoad(newData);
    }
  };

  const updateSection = (index: number, field: keyof TextAreaSection, value: string): void => {
    const newSections = [...data.sections];
    newSections[index] = { ...newSections[index], [field]: value } as TextAreaSection;
    const newData = { ...data, sections: newSections };
    setData(newData);
    notifyComponentLoad(newData);
  };

  return (
    <LoadingErrorWrapper loading={false} error={null} data={data}>
      <TextAreaView
        sx={sx || {}}
        data={data}
        onAddSection={addSection}
        onRemoveSection={removeSection}
        onUpdateSection={updateSection}
        isEdit={true}
      />
    </LoadingErrorWrapper>
  );
};

// Thumbnail version for sidebar - no editing functionality
export const TextAreaThumbnail = ({ sx }: ComponentEditProps<TextAreaData>) => {
  return (
    <Box sx={{ ...styles.thumbnailContainer, ...sx } as SxProps}>
      <Typography variant='subtitle2' sx={styles.thumbnailTitle}>
        Title
      </Typography>
      <Box sx={styles.thumbnailContentBox}>
        <Typography variant='body2' sx={styles.thumbnailContent}>
          Text content
        </Typography>
      </Box>
    </Box>
  );
};

export const TextAreaComponent: ComponentDefinition<TextAreaData> = {
  id: COMPONENT_ID,
  name: 'Text Area',
  type: SmartReportComponentTypesEnum.TextArea,
  reportTypes: Object.values(SmartReportTypeEnum),
  Thumbnail: TextAreaThumbnail,
  Component: TextArea,
  ComponentView: TextAreaView,
};
